<template>
  <main role="main" class="py-4">
    <b-container fluid="xxl" class="mb-3">
      <div class="d-flex flex-row align-items-center justify-content-between mb-4">
        <h4 class="m-0">{{ $t("saleAccountList") }}</h4>
        <div>
          <b-button variant="outline-secondary" class="mr-4" @click="onClickReloadBtn">
            <b-icon icon="arrow-clockwise"></b-icon> {{ $t("reload") }}
          </b-button>
          <b-button variant="outline-primary" v-b-modal:new-sale-account-modal>
            <b-icon icon="plus"></b-icon> {{ $t("createNew") }}
          </b-button>
        </div>
      </div>

      <b-form class="d-flex flex-row align-items-center mb-3"
              @submit.prevent="onFilterFormSubmit">
        <div class="flex-shrink-0 mr-3">{{ $t("saleArea2") }}:</div>
        <b-form-select class="mr-5" :options="areaFilterOptions" v-model="areaFilterSelectedOption"/>
        <div class="flex-shrink-0 mr-3">{{ $t("search") }}:</div>
        <b-form-input type="search" v-model="filterText" minlength="2" maxlength="50" :placeholder="$t('adminSearchPlaceholder')" class="mr-5" />
        <b-button type="submit" class="flex-shrink-0" variant="primary">{{ $t("search") }}</b-button>
      </b-form>

      <b-table ref="table" :items="tableItemProvider" hover primary-key="id" :fields="tableFields" show-empty
               :per-page="itemsPerPage" :current-page="currentPage">
        <template v-slot:table-busy>
          <div class="text-center text-secondary py-4">{{ $t("loadingData") }}</div>
        </template>
        <template v-slot:empty>
          <div class="text-center text-secondary py-4">{{ $t("noData") }}</div>
        </template>
        <template v-slot:cell(name)="data">
          {{ data.item.name }}
        </template>
        <template v-slot:cell(actions)="data">
          <b-dropdown size="lg" variant="link" toggle-class="text-decoration-none p-0" no-caret right menu-class="shadow">
            <template v-slot:button-content>
              <b-icon icon="three-dots"/>
            </template>
            <b-dropdown-item-button v-b-modal:edit-sale-account-modal @click="onClickEditSaleAccount(data.item)">
              {{ $t("updateInfo") }}
            </b-dropdown-item-button>
            <b-dropdown-item-button v-b-modal:reset-password-modal @click="onClickResetSaleAccountPassword(data.item)">
              {{ $t("resetPassword") }}
            </b-dropdown-item-button>
            <b-dropdown-item-button @click="onClickDeleteSaleAccount(data.item)">{{ $t("actionDelete") }}</b-dropdown-item-button>
          </b-dropdown>
        </template>
      </b-table>

      <div class="d-flex flex-row align-items-center justify-content-between">
        <div></div>
        <div class="d-flex flex-row align-items-center">
          <div v-if="totalItems" class="text-right flex-shrink-0 mr-3">{{ $t("resultCount", { count: totalItems }) }}</div>
          <b-pagination class="my-0 mr-3" v-model="currentPage" :total-rows="totalItems" :per-page="itemsPerPage"></b-pagination>
          <b-form-select v-model="itemsPerPage" :options="itemsPerPageSelections"></b-form-select>
        </div>
      </div>
    </b-container>

    <b-modal id="new-sale-account-modal" :title="$t('createSaleAccount')"
             dialog-class="min-width-700" hide-footer no-close-on-backdrop>
      <NewSaleAccountModal @submitted="refreshData" />
    </b-modal>

    <b-modal id="edit-sale-account-modal" :title="$t('updateAccountInfo')"
             hide-footer no-close-on-backdrop>
      <EditSaleAccountModal :editing-user="userToEdit" @submitted="refreshData" />
    </b-modal>

    <b-modal id="reset-password-modal" :title="$t('resetPassword')"
             hide-footer no-close-on-backdrop>
      <ResetPasswordModal :target-user="userToEdit" @submitted="refreshData" />
    </b-modal>

  </main>
</template>

<style lang="scss" scoped>

</style>

<script lang="ts">
  import {Component, Ref, Vue} from 'vue-property-decorator';
  import _ from "lodash";
  import BaseComponent from "@/components/BaseComponent";
  import {BvTableFieldArray} from "bootstrap-vue";
  import {BTable, BvTableCtxObject, BvTableProviderPromiseResult} from "bootstrap-vue/src/components/table";

  import * as util from "@/service/util";
  import EditSaleAccountModal from "@/components/sale-account/EditSaleAccountModal.vue";
  import NewSaleAccountModal from "@/components/sale-account/NewSaleAccountModal.vue";
  import ResetPasswordModal from "@/components/account/ResetPasswordModal.vue";
  import * as userService from "@/service/userService";
  import * as areaService from "@/service/areaService";


  @Component({
    components: { NewSaleAccountModal, EditSaleAccountModal, ResetPasswordModal }
  })
  export default class SaleAccounts extends BaseComponent {

    areaFilterSelectedOption: number = null;
    areaFilterOptions = [];
    filterText = "";

    itemsPerPageSelections = [
      { value: 10, text: "10 " + this.$t("itemsPerPage").toString() },
      { value: 20, text: "20 " + this.$t("itemsPerPage").toString() },
      { value: 50, text: "50 " + this.$t("itemsPerPage").toString() },
      { value: 100, text: "100 " + this.$t("itemsPerPage").toString() },
    ];
    itemsPerPage = 50;
    currentPage = 1;
    totalItems = 0;

    @Ref("table") table: BTable;
    tableFields: BvTableFieldArray = [
      {
        key: "id",
        label: "#"
      },
      {
        key: "name",
        label: this.$t("fullName").toString(),
        thClass: "text-nowrap",
        tdClass: "text-break",
      },
      {
        key: "username",
        label: "Username",
        thClass: "text-nowrap",
        tdClass: "text-break",
      },
      {
        key: "phone",
        label: this.$t("phoneNumber").toString(),
      },
      {
        key: "email",
        label: "Email",
        thClass: "text-nowrap",
        tdClass: "text-break",
      },
      {
        key: "area",
        label: this.$t("saleArea").toString(),
        thClass: "text-nowrap",
        tdClass: "text-break",
      },
      {
        key: "creatorName",
        label: this.$t("creator").toString(),
        thClass: "text-nowrap",
        tdClass: "text-break",
      },
      {
        key: "createdAt",
        label: this.$t("createdDate").toString(),
      },
      {
        key: "actions",
        label: ""
      }
    ];

    userToEdit = null;


    mounted() {
      this.fetchAreas();
    }

    async fetchAreas() {
      try {
        const areas = await areaService.getAll();
        this.areaFilterOptions = areas.map(it => ({ value: it.id, text: it.name }));
        this.areaFilterOptions.unshift({ value: null, text: this.$t("all").toString() });

      } catch (e) {
        this.toastError(e);
      }
    }

    onClickReloadBtn() {
      this.filterText = "";
      this.areaFilterSelectedOption = null;
      this.currentPage = 1;

      this.fetchAreas();
      this.table.refresh();
    }

    onFilterFormSubmit() {
      this.table.refresh();
    }

    refreshData() {
      this.table.refresh();
    }

    async tableItemProvider(ctx: BvTableCtxObject): Promise<BvTableProviderPromiseResult> {
      const fetchSaleAccountRes = await userService.getSaleAccounts({
        page: ctx.currentPage,
        pageSize: this.itemsPerPage,
        q: this.filterText?.trim(),
        areaId: this.areaFilterSelectedOption
      });

      this.totalItems = fetchSaleAccountRes.total;
      return fetchSaleAccountRes.data;
    }

    onClickEditSaleAccount(user) {
      this.userToEdit = user;
    }

    onClickResetSaleAccountPassword(user) {
      this.userToEdit = user;
    }


    async onClickDeleteSaleAccount(user) {
      const modalMessage = this.$t("deleteAccountConfirmation", { account: user.name }).toString() + `(ID: ${user.id})?`;
      const confirmDelete = await this.$bvModal.msgBoxConfirm(modalMessage, {
        okVariant: 'danger',
        okTitle: this.$t("yes").toString(),
        cancelTitle: this.$t("no").toString(),
      });

      if (confirmDelete) {
        try {
          this.showLoading();
          await userService.deleteUser(user.id);
          this.onClickReloadBtn();
          this.toastSuccessDelay(this.$t("deleteAccountSuccess").toString());

        } catch (e) {
          this.toastError(e);

        } finally {
          this.hideLoading();
        }
      }
    }

  }
</script>
